import { FlatFileData, FlatFileManagerSettings } from "./types";
import { useFlatFileExplorer } from "./useFlatFileExplorer";
import {
  FileManagerComponent,
  NavigationPane,
  DetailsView,
  Toolbar,
  Inject,
} from "@syncfusion/ej2-react-filemanager";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import classNames from "classnames";
import {
  getDefaultFlatFileExplorerToolbarItems,
  combineFlatFileExplorerToolbarItems,
  renderFlatFileExplorerToolbarItems,
} from "./useFlatFileExplorerToolbarItems";
import { TextField, TextFieldValue } from "../../..";

export interface FlatFileExplorerProps<T> {
  data?: FlatFileData<T>[];
  fileManagerSettings?: FlatFileManagerSettings<T>;
  navigationPaneOnly?: boolean;
  additionalToolbarItems?: any[]; // Toolbar items from parent components like DocLibrary
}

// Define the interface for the exposed methods
export interface FlatFileExplorerRef1<T> {
  refresh: () => void;
  refreshLayout: () => void;
  refreshFiles: () => void;
  getSelectedItems: () => FlatFileData<T>[];
  getSelectedData: () => T | undefined;
  getFileManagerRef: () => FileManagerComponent | null;
  onFolderSelected?: () => void;
}

export const FlatFileExplorer = forwardRef(
  <T,>(
    props: FlatFileExplorerProps<T>,
    ref: React.Ref<FlatFileExplorerRef1<T>>
  ) => {
    const [filesData, setFilesData] = useState<{ [key: string]: Object }[]>([]);
    const [renderKey, setRenderKey] = useState("init-key");
    const [selectedData, setSelectedData] = useState<T | undefined>(undefined);
    const { ready, settingsRef, flatFileExplorerRef, fieldKey, searchFolder } =
      useFlatFileExplorer(props, setFilesData, setRenderKey, setSelectedData);

    // Combine FlatFileExplorer toolbar items with additional toolbar items from parent
    const allToolbarItems = React.useMemo(() => {
      const defaultToolbarItems = getDefaultFlatFileExplorerToolbarItems<T>(
        searchFolder,
        props.fileManagerSettings?.onNewFolderClick
      );

      const combined = combineFlatFileExplorerToolbarItems(
        defaultToolbarItems,
        props.additionalToolbarItems || []
      );

      return combined;
    }, [
      searchFolder,
      props.fileManagerSettings?.onNewFolderClick,
      props.additionalToolbarItems,
    ]);

    // Update settings to include toolbar items
    if (settingsRef.current) {
      (settingsRef.current as any).toolbarSettings = {
        visible: allToolbarItems.length > 0,
        items: [],
      };
      (settingsRef.current as any).toolbarItems = allToolbarItems;
    }

    // Render toolbar items when component is ready or selection changes
    React.useEffect(() => {
      if (ready) {
        renderFlatFileExplorerToolbarItems(allToolbarItems, selectedData);
      }
    }, [ready, allToolbarItems, selectedData]);

    useImperativeHandle(
      ref,
      () => ({
        refresh: () => {
          flatFileExplorerRef.current?.refresh();
        },
        refreshLayout: () => {
          flatFileExplorerRef.current?.refreshLayout();
        },
        refreshFiles: () => {
          flatFileExplorerRef.current?.refreshFiles();
        },
        getSelectedItems: () => {
          return (
            (flatFileExplorerRef.current?.selectedItems as FlatFileData<T>[]) ||
            []
          );
        },
        getSelectedData: () => {
          return selectedData;
        },
        getFileManagerRef: () => {
          return flatFileExplorerRef.current;
        },
        // Add any other methods you want to expose
      }),
      [flatFileExplorerRef]
    );

    return (
      <div>
        <section
          className={classNames("ucl-flat-file-explorer-container", {
            "ucl-flat-file-explorer-container-hide-address":
              !props.fileManagerSettings?.showAddressBar || false,
            "ucl-flat-file-explorer-container-hide-upload":
              !props.fileManagerSettings?.allowUpload || false,
            "ucl-flat-file-explorer-navigation-pane-only":
              props.navigationPaneOnly || false,
          })}
          key={fieldKey.current}
        >
          <>
            {ready && filesData.length > 0 && (
              <div className="ucl-flat-file-explorer-container-content">
                <TextField
                  className="ucl-flat-file-explorer-search"
                  placeholder="Search Folder Name"
                  onSubmit={(value: TextFieldValue) => {
                    searchFolder(value || "");
                  }}
                />
                <div
                  className="ucl-flat-file-explorer-container-content-body"
                  key={renderKey}
                >
                  <FileManagerComponent
                    ref={flatFileExplorerRef}
                    {...(() => {
                      const {
                        allowUpload,
                        defaultOpenFolderId,
                        showAddressBar,
                        onPinClick,
                        allowPinnedFolders,
                        maxPinnedFolders,
                        onNewFolderClick,
                        ...rest
                      } = settingsRef.current as FlatFileManagerSettings<T>;
                      return rest;
                    })()}
                    fileSystemData={filesData as { [key: string]: Object }[]}
                  >
                    {(() => {
                      const services = props.navigationPaneOnly
                        ? [NavigationPane, DetailsView] // Breaks w/o DetailsView injected
                        : [NavigationPane, DetailsView, Toolbar];
                      return <Inject services={services} />;
                    })()}
                  </FileManagerComponent>
                </div>
              </div>
            )}
            {!ready || (filesData.length === 0 && <></>)}
          </>
        </section>
      </div>
    );
  }
);

export default FlatFileExplorer;
